import { useState, useContext } from 'react'
import { db } from '../../firebase/config'
import { SavedIcon } from '../../StateManagment/SavedIcon'
import { useFirestoreGeneralOrderBy } from '../../firebase/useFirestore'
import eligibleTriggerFields from '../../helpers/questionnaire/eligibleTriggerFields'
import isStandardQuestion from '../../helpers/questionnaire/isStandardQuestion'
import isQualifyingQuestion from '../../helpers/questionnaire/isQualifyingQuestion'

// Het 'Zichtbaarheid'-blok in de vraag-editor: laat een vraag afhangen van het
// antwoord op een eerdere vraag.
//
// SCHRIJFT BIJ ELKE WIJZIGING WEG, ook als de regel nog niet af is. Er is geen
// 'Opslaan'-knop in deze editor, dus wachten op een complete regel zou de
// keuze van de gebruiker laten verdampen. Een halve regel is onschadelijk:
// evaluateShowWhen toont bij een lege waardenlijst.
// AI-CONTEXT: See docs/pitfalls.md#ui-strict-validator-kills-autosave
// AI-CONTEXT: See docs/features/conditional-questions.md

/** Welke voorwaarden passen bij het type van de TRIGGERvraag. */
const OPERATORS_BY_TYPE = {
    'multiple-one': [['is', 'is'], ['is-niet', 'is niet']],
    'multiple-multiple': [['is', 'is'], ['is-niet', 'is niet']],
    'matrix-one': [['is', 'is'], ['is-niet', 'is niet']],
    scale: [['is', 'is'], ['gte', 'is minstens'], ['lte', 'is hoogstens']],
    number: [['is', 'is'], ['gte', 'is minstens'], ['lte', 'is hoogstens']],
    paragraph: [['is-ingevuld', 'is ingevuld'], ['is-leeg', 'is leeg']],
}

const CHOICE_TYPES = new Set(['multiple-one', 'multiple-multiple'])
const NUMERIC_TYPES = new Set(['scale', 'number'])

// Een naamloze optie, stelling of kolom levert anders een keuzelijst met lege
// regels op: onbruikbaar. Komt echt voor — matrixkolommen worden vaak niet
// benoemd.
const NAMELESS = {
    option: '(naamloze antwoordoptie)',
    row: '(naamloze stelling)',
    column: '(naamloos antwoord)',
}

/**
 * @param {string} type - Type van de triggervraag
 * @returns {string} De voorwaarde die standaard geselecteerd staat
 */
const defaultOperator = (type) => (OPERATORS_BY_TYPE[type]?.[0]?.[0] || 'is')

/**
 * @typedef {object} ShowWhenRule
 * @property {string} FieldID - `QuestionnaireFields.ID` van de triggervraag
 * @property {string} Operator - is | is-niet | gte | lte | is-ingevuld | is-leeg
 * @property {Array<string>} Values - optie-ID's, kolom-ID's of één drempelgetal
 * @property {string} [RowID] - `MatrixQuestionRows.ID`, alleen bij een matrixtrigger
 */

/**
 * @param {object} props
 * @param {object} props.field - Het QuestionnaireFields-doc dat bewerkt wordt
 * @param {Array<object>} props.allFields - Alle velden van de vragenlijst, voor
 *   het bepalen van de bruikbare triggervragen
 */
const VisibilityRule = ({ field, allFields }) => {
    // Context
    const [, setSaved] = useContext(SavedIcon)

    // De regel zoals hij nu opgeslagen staat
    const rule = field?.ShowWhen || null
    const triggerId = rule?.FieldID || ''

    // BEWUST GEEN useEffect die dit met de Firestore-snapshot synchroniseert:
    // elke snapshot zou de radioknop terugzetten terwijl de gebruiker bezig is.
    // AI-CONTEXT: See docs/pitfalls.md#state-draft-wiped-by-firestore-snapshot
    const [wantsRule, setWantsRule] = useState(Boolean(triggerId))

    const triggerField = (allFields || []).find((item) => item?.ID === triggerId)
    const triggerType = triggerField?.Type || ''

    // Hooks staan vóór elke vroege return (regels der hooks). Een lege ID levert
    // een lege lijst — nooit undefined doorgeven aan een where().
    // AI-CONTEXT: See docs/pitfalls.md#database-firestore-where-undefined-rejects
    const optionsId = CHOICE_TYPES.has(triggerType) ? triggerId : ''
    const matrixId = triggerType === 'matrix-one' ? triggerId : ''
    const options = useFirestoreGeneralOrderBy('MultipleQuestionOptions', 'Field', optionsId, 'Position', 'asc')
    const matrixRows = useFirestoreGeneralOrderBy('MatrixQuestionRows', 'Field', matrixId, 'Position', 'asc')
    const matrixColumns = useFirestoreGeneralOrderBy('MatrixQuestionColumns', 'Field', matrixId, 'Position', 'asc')

    // Een meetstandaardvraag wordt op elk meetmoment gesteld en mag dus niet
    // conditioneel worden — het effect wordt afgemeten aan voor- én nameting.
    // Een kwalificeringsvraag (of haar sectie) wordt altijd gesteld.
    if (isStandardQuestion(field) || isQualifyingQuestion(field)) return null

    const triggers = eligibleTriggerFields(allFields, field)

    /**
     * @param {ShowWhenRule|null} next - de nieuwe regel, of null om hem te wissen
     */
    const writeRule = (next) => {
        db.collection('QuestionnaireFields')
            .doc(field.docid)
            .update({ ShowWhen: next })
            .then(() => setSaved('flex'))
    }

    const alwaysHandler = () => {
        setWantsRule(false)
        writeRule(null)
    }

    const triggerHandler = (e) => {
        const nextId = e.target.value
        if (!nextId) {
            writeRule(null)
            return
        }
        const nextType = (allFields || []).find((item) => item?.ID === nextId)?.Type || ''
        writeRule({ FieldID: nextId, Operator: defaultOperator(nextType), Values: [] })
    }

    const operatorHandler = (e) => writeRule({ ...rule, Operator: e.target.value })

    const valueHandler = (e) => {
        const value = e.target.value
        writeRule({ ...rule, Values: value === '' ? [] : [value] })
    }

    const rowHandler = (e) => writeRule({ ...rule, RowID: e.target.value })

    const operators = OPERATORS_BY_TYPE[triggerType] || []
    const needsValue = triggerType !== '' && triggerType !== 'paragraph'

    return (
        <div className="qb-visibility">
            <span className="qb-field-label">Zichtbaarheid</span>

            {triggers.length === 0 ? (
                <p className="qb-visibility-empty">
                    Deze vraag kan niet afhangen van een antwoord: er staat geen vraag vóór deze.
                </p>
            ) : (
                <>
                    <div className="qb-visibility-modes">
                        <label className="qb-visibility-mode">
                            <input
                                type="radio"
                                name={`visibility-${field.ID}`}
                                aria-label="Altijd tonen"
                                checked={!wantsRule}
                                onChange={alwaysHandler}
                            />
                            <span>Altijd tonen</span>
                        </label>
                        <label className="qb-visibility-mode">
                            <input
                                type="radio"
                                name={`visibility-${field.ID}`}
                                aria-label="Alleen tonen als…"
                                checked={wantsRule}
                                onChange={() => setWantsRule(true)}
                            />
                            <span>Alleen tonen als…</span>
                        </label>
                    </div>

                    {wantsRule && (
                        <div className="qb-visibility-rule">
                            <select aria-label="Triggervraag" value={triggerId} onChange={triggerHandler}>
                                <option value="">-- Kies een vraag --</option>
                                {triggers.map((item) => (
                                    <option key={item.ID} value={item.ID}>{item.Question}</option>
                                ))}
                            </select>

                            {triggerType === 'matrix-one' && (
                                <select aria-label="Stelling" value={rule?.RowID || ''} onChange={rowHandler}>
                                    <option value="">-- Kies een stelling --</option>
                                    {(matrixRows || []).map((row) => (
                                        <option key={row.ID} value={row.ID}>{row.Title || NAMELESS.row}</option>
                                    ))}
                                </select>
                            )}

                            {operators.length > 0 && (
                                <select aria-label="Voorwaarde" value={rule?.Operator || ''} onChange={operatorHandler}>
                                    {operators.map(([value, label]) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                            )}

                            {needsValue && NUMERIC_TYPES.has(triggerType) && (
                                <input
                                    type="number"
                                    aria-label="Antwoord"
                                    defaultValue={rule?.Values?.[0] ?? ''}
                                    onChange={valueHandler}
                                />
                            )}

                            {needsValue && CHOICE_TYPES.has(triggerType) && (
                                <select aria-label="Antwoord" value={rule?.Values?.[0] || ''} onChange={valueHandler}>
                                    <option value="">-- Kies een antwoord --</option>
                                    {(options || []).map((option) => (
                                        <option key={option.ID} value={option.ID}>
                                            {option.Option === 'other' ? 'Anders, namelijk...' : (option.Option || NAMELESS.option)}
                                        </option>
                                    ))}
                                </select>
                            )}

                            {needsValue && triggerType === 'matrix-one' && (
                                <select aria-label="Antwoord" value={rule?.Values?.[0] || ''} onChange={valueHandler}>
                                    <option value="">-- Kies een antwoord --</option>
                                    {(matrixColumns || []).map((column) => (
                                        <option key={column.ID} value={column.ID}>{column.Title || NAMELESS.column}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default VisibilityRule
